import React from "react";
import {
	Container, Box,
	Typography, Button
} from "@mui/material";
import { TextFieldControl } from "@haohan/form-ui";
import { useForm } from "react-hook-form";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import InfoIcon from '@mui/icons-material/Info';
import PasswordIcon from '@mui/icons-material/Password';

import { SiteAuthHeader } from "../../components/AuthHeader";
import { SiteRouterLink } from "../../../components/RouterLink";

import { AuthPaper } from "../../components/AuthPaper";

import { useI18n } from './locales';
import { Helmet } from "react-helmet-async";
import { papi } from "@haohan-frontend/papi";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useUrlWithNext } from "../../utils";

export function AuthLogin() {
	const { t, Trans, lang } = useI18n();
	const { control, formState, handleSubmit, setError } = useForm({
		defaultValues: {
			email: '',
			password: '',
			// email: 'test@mail.com',
			// password: 'qwerty123',
		},
		mode: 'onChange',
	});

	const navigate = useNavigate();
	const verifyURL = useUrlWithNext('/auth/email/verify', true);
	const registerURL = useUrlWithNext('/auth/register');
	const forgotPasswordURL = useUrlWithNext('/auth/password/forgot');

	const submitForm = handleSubmit(async data => {
		try {
			const res = await papi.auth.login(data);
			if (res.account) {
				if (!res.account.isEmailVerified) {
					navigate(verifyURL);
				} else {
					let url = `/${lang}`;
					const nextPath = new URLSearchParams(location.search)
						.get('next');
					if (nextPath) {
						url = nextPath
					}
					window.location.href = new URL(url, location.href)
						.toString();
				}
			}
		} catch (e) {
			const err = e as AxiosError;
			if (err.response.status === 401) {
				setError('email', {
					message: t('errors.invalid_credentials')
				})
			}
		}
	});

	return (
		<>
			<Helmet>
				<title>{t('Sign in account')} — Haohan</title>
			</Helmet>
			<SiteAuthHeader/>
			<main>
				<Container sx={{ py: 6 }}>
					<AuthPaper
						leftColumn={{
							title: t('instructions.intro'),
							items: [
								{
									icon: <AlternateEmailIcon color='disabled' fontSize='small'/>,
									title: t('instructions.step_1_email'),
								},
								{
									icon: <PasswordIcon color='disabled' fontSize='small'/>,
									title: t('instructions.step_2_password'),
								},
								{
									icon: <AccountCircleIcon color='disabled' fontSize='small'/>,
									title: t('instructions.step_3_access'),
								},
								{
									icon: <InfoIcon color='info' fontSize='small'/>,
									title: (
										<Trans
											i18nKey='instructions.step_4_recover'
											components={[
												<SiteRouterLink
													underline='none'
													to={forgotPasswordURL}
												/>
											]}
										/>
									),
									mt: 4
								},
							]
						}}
						rightColumn={(
							<>
								<Typography
									display='block'
									fontWeight='bold'
									mb={.5}
									textAlign='center'
									variant='h6'
								>
									{t('Sign in account')}
								</Typography>
								<Typography
									color='textSecondary'
									display='block'
									mb={3}
									textAlign='center'
									variant='body2'
								>
									{t('To log into your account, use the email and password you provided during registration')}
								</Typography>
								<form onSubmit={submitForm}>
									<TextFieldControl
										autoComplete='username'
										control={control}
										fullWidth
										inputMode='email'
										label='Email'
										margin='dense'
										name='email'
										rules={{validate(v) {
											if (!v) {
												return 'Обязательное поле'
											}
										}}}
										size='small'
										variant='outlined'
									/>
									<TextFieldControl
										autoComplete='current-password'
										control={control}
										fullWidth
										label={t('Password')}
										margin='normal'
										name='password'
										rules={{validate(v) {
											if (!v) {
												return 'Обязательное поле'
											}
										}}}
										size='small'
										type='password'
										variant='outlined'
									/>
									<Box textAlign='right'>
										<SiteRouterLink
											to={forgotPasswordURL}
											underline='hover'
											variant='body2'
										>
											{t('Forgot password?')}
										</SiteRouterLink>
									</Box>

									<Box mt={5} mb={1}>
										<Button
											disabled={formState.isSubmitting}
											fullWidth
											type="submit"
											variant='contained'
										>
											{t('actions.Sign in')}
										</Button>
									</Box>
									<Box textAlign='center'>
										<Typography color='textSecondary' variant='body2'>
											{t('Not a member?')}{' '}
											<SiteRouterLink
												to={registerURL}
												underline='hover'
											>
												{t('actions.Register')}
											</SiteRouterLink>
										</Typography>
									</Box>
								</form>
							</>
						)}
					/>
				</Container>
			</main>
		</>
	);
}
